import * as React from "react";
import { CircleHelp, Laptop, Smartphone, Tablet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { fetchWithCsrf } from "./fetchWithCsrf";
import { authClient } from "./authClient";

type AccountSessionDevice = {
  id: string;
  token: string;
  isCurrent: boolean;
  deviceName: string;
  deviceKind: "desktop" | "mobile" | "tablet" | "unknown";
  browser: string | null;
  lastActiveLabel: string;
  updatedAt: string;
  location: string;
  ipAddress: string | null;
};

const COLLAPSED_COUNT = 3;

const DeviceIcon = ({ kind }: { kind: AccountSessionDevice["deviceKind"] }): React.ReactElement => {
  const className = "size-4 shrink-0 text-muted-foreground";
  switch (kind) {
    case "mobile":
      return <Smartphone className={className} aria-hidden />;
    case "tablet":
      return <Tablet className={className} aria-hidden />;
    case "desktop":
      return <Laptop className={className} aria-hidden />;
    case "unknown":
      return <Laptop className={className} aria-hidden />;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};

const HelpTip = ({ label }: { label: string }): React.ReactElement => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
        aria-label={label}
      >
        <CircleHelp className="size-3.5" aria-hidden />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[240px] text-xs">
      {label}
    </TooltipContent>
  </Tooltip>
);

export const AccountDevices = (): React.ReactElement => {
  const { t } = useTranslation();
  const [sessions, setSessions] = React.useState<AccountSessionDevice[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingToken, setPendingToken] = React.useState<string | null>(null);
  const [pendingAll, setPendingAll] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const loadSessions = React.useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const response = await fetchWithCsrf("/api/account/sessions", {
        method: "GET",
        credentials: "include"
      });
      if (!response.ok) {
        setError(t("account.devicesLoadFailed"));
        setSessions([]);
        return;
      }
      const body = (await response.json()) as { sessions?: AccountSessionDevice[] };
      setSessions(Array.isArray(body.sessions) ? body.sessions : []);
    } catch {
      setError(t("account.devicesLoadFailed"));
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const revokeOne = async (token: string): Promise<void> => {
    if (!window.confirm(t("account.devicesLogoutConfirm"))) return;
    setPendingToken(token);
    setError(null);
    try {
      const result = await authClient.$fetch("/revoke-session", {
        method: "POST",
        body: { token }
      });
      if (result.error) {
        setError(t("account.devicesLogoutFailed"));
        return;
      }
      setSessions((current) => current.filter((session) => session.token !== token));
    } catch {
      setError(t("account.devicesLogoutFailed"));
    } finally {
      setPendingToken(null);
    }
  };

  const revokeOthers = async (): Promise<void> => {
    if (!window.confirm(t("account.devicesLogoutAllConfirm"))) return;
    setPendingAll(true);
    setError(null);
    try {
      const result = await authClient.$fetch("/revoke-other-sessions", {
        method: "POST"
      });
      if (result.error) {
        setError(t("account.devicesLogoutAllFailed"));
        return;
      }
      setSessions((current) => current.filter((session) => session.isCurrent));
      setExpanded(false);
    } catch {
      setError(t("account.devicesLogoutAllFailed"));
    } finally {
      setPendingAll(false);
    }
  };

  const canCollapse = sessions.length > COLLAPSED_COUNT;
  const visibleSessions =
    canCollapse && !expanded ? sessions.slice(0, COLLAPSED_COUNT) : sessions;
  const otherCount = sessions.filter((session) => !session.isCurrent).length;

  return (
    <TooltipProvider delayDuration={200}>
      <Card id="devices" className="scroll-mt-24">
        <CardHeader>
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-base">{t("account.devicesTitle")}</CardTitle>
            <HelpTip label={t("account.devicesHelp")} />
          </div>
          <CardDescription>{t("account.devicesDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium">{t("account.devicesLogoutAllTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("account.devicesLogoutAllDesc")}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={pendingAll || otherCount === 0 || loading}
              onClick={() => void revokeOthers()}
            >
              {pendingAll ? t("account.devicesLoggingOut") : t("account.devicesLogoutAllAction")}
            </Button>
          </div>

          <Separator />

          {loading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : null}

          {!loading && sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("account.devicesEmpty")}</p>
          ) : null}

          {!loading && sessions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t("account.devicesColName")}</TableHead>
                  <TableHead>
                    <span className="inline-flex items-center gap-1">
                      {t("account.devicesColLastActive")}
                      <HelpTip label={t("account.devicesLastActiveHelp")} />
                    </span>
                  </TableHead>
                  <TableHead>{t("account.devicesColLocation")}</TableHead>
                  <TableHead className="w-[1%] text-right">
                    <span className="sr-only">{t("account.devicesColActions")}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleSessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      <div className="flex min-w-0 items-start gap-2.5">
                        <span className="mt-0.5">
                          <DeviceIcon kind={session.deviceKind} />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium">{session.deviceName}</p>
                          {session.isCurrent ? (
                            <p className="mt-0.5 text-xs font-medium text-primary">
                              {t("account.devicesThisDevice")}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {session.isCurrent ? t("account.devicesNow") : session.lastActiveLabel}
                    </TableCell>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="truncate text-muted-foreground" title={session.location}>
                          {session.location}
                        </p>
                        {session.ipAddress ? (
                          <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground/80">
                            {session.ipAddress}
                          </p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {session.isCurrent ? null : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={pendingToken === session.token}
                          onClick={() => void revokeOne(session.token)}
                        >
                          {pendingToken === session.token
                            ? t("account.devicesLoggingOut")
                            : t("account.devicesLogout")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {canCollapse ? (
            <button
              type="button"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? t("account.devicesShowFewer") : t("account.devicesShowAll")}
            </button>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
